import type { GatewayEvent } from './types';

export interface SendMessageInput {
  gatewayUrl: string;
  company: string;
  channelId: string;
  userId: string;
  sessionId: string;
  content: string;
}

export interface PollEventsInput {
  gatewayUrl: string;
  channelId: string;
  userId: string;
  cursor: number;
}

export interface WebSocketEventsInput {
  gatewayUrl: string;
  channelId: string;
  userId: string;
  onEvent: (event: GatewayEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: () => void;
}

export interface ChannelConfigInput {
  gatewayUrl: string;
  company: string;
  channelId: string;
}

export interface ChannelConfigResponse {
  channelId: string;
  company: string;
  mode?: 'widget' | 'spotlight';
  position?: string;
  title?: string;
  placeholder?: string;
  sendButtonText?: string;
  launcherSymbol?: string;
  launcherIconUrl?: string;
  theme?: {
    accentColor?: string;
    surfaceColor?: string;
    textColor?: string;
    headerTextColor?: string;
    userBubbleColor?: string;
    userTextColor?: string;
    assistantBubbleColor?: string;
    assistantTextColor?: string;
    composerBackgroundColor?: string;
    inputBackgroundColor?: string;
    inputTextColor?: string;
    sendButtonTextColor?: string;
  };
  ui?: {
    panelWidth?: number;
    panelHeight?: number;
    panelRadius?: number;
    bubbleRadius?: number;
    inputRadius?: number;
    fontSize?: number;
    launcherSize?: number;
    launcherShape?: 'circle' | 'rounded-square' | 'squircle' | 'hexagon';
    launcherBackgroundColor?: string;
  };
}

export async function sendMessage(input: SendMessageInput): Promise<void> {
  const url = new URL('/api/v1/webchat/messages', input.gatewayUrl);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      company: input.company,
      channelId: input.channelId,
      userId: input.userId,
      sessionId: input.sessionId,
      content: input.content,
    }),
  });

  if (!res.ok) {
    throw new Error('SEND_MESSAGE_FAILED');
  }
}

export async function sendTyping(gatewayUrl: string, company: string, channelId: string, userId: string): Promise<void> {
  const url = new URL('/api/v1/webchat/typing', gatewayUrl);
  await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company, channelId, userId }),
  });
}

export async function fetchChannelConfig(input: ChannelConfigInput): Promise<ChannelConfigResponse> {
  const url = new URL('/api/v1/webchat/channel-config', input.gatewayUrl);
  url.searchParams.set('company', input.company);
  url.searchParams.set('channelId', input.channelId);
  const res = await fetch(url.toString(), { method: 'GET' });
  if (!res.ok) {
    throw new Error('FETCH_CHANNEL_CONFIG_FAILED');
  }
  return (await res.json()) as ChannelConfigResponse;
}

export async function pollEvents(input: PollEventsInput): Promise<{ cursor: number; events: GatewayEvent[] }> {
  const url = new URL('/api/v1/webchat/events/poll', input.gatewayUrl);
  url.searchParams.set('channelId', input.channelId);
  url.searchParams.set('userId', input.userId);
  url.searchParams.set('cursor', String(input.cursor));

  const res = await fetch(url.toString(), { method: 'GET' });
  if (!res.ok) {
    throw new Error('POLL_EVENTS_FAILED');
  }

  return (await res.json()) as { cursor: number; events: GatewayEvent[] };
}

function toWsUrl(gatewayUrl: string, channelId: string, userId: string): string {
  const url = new URL('/ws', gatewayUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('channelId', channelId);
  url.searchParams.set('userId', userId);
  return url.toString();
}

export function connectEventsSocket(input: WebSocketEventsInput): () => void {
  const ws = new WebSocket(toWsUrl(input.gatewayUrl, input.channelId, input.userId));

  ws.onopen = () => {
    input.onOpen?.();
  };

  ws.onclose = () => {
    input.onClose?.();
  };

  ws.onerror = () => {
    input.onError?.();
  };

  ws.onmessage = (message: MessageEvent) => {
    try {
      const event = JSON.parse(String(message.data)) as GatewayEvent;
      input.onEvent(event);
    } catch {
      // no-op
    }
  };

  return () => {
    try {
      ws.close();
    } catch {
      // no-op
    }
  };
}
